/**
 * Recursively optional over the keys the base tree has. Arrays (e.g. in the
 * `terms` namespace) are replaced wholesale, so an override gives the full list.
 */
export type DeepPartial<T> = T extends readonly unknown[]
  ? T
  : T extends object
    ? { [K in keyof T]?: DeepPartial<T[K]> }
    : T;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Deep-merges app overrides onto a shared resource tree. Plain objects recurse
 * so untouched siblings survive; strings, arrays and everything else replace.
 * A key absent from the override, or `undefined`, keeps the base value.
 */
export function deepMergeResources<T>(base: T, override?: DeepPartial<T>): T {
  if (override === undefined) {
    return base;
  }

  if (!isPlainObject(base) || !isPlainObject(override)) {
    return override as T;
  }

  const merged: Record<string, unknown> = { ...base };

  for (const [key, value] of Object.entries(override)) {
    if (value !== undefined) {
      merged[key] = deepMergeResources(merged[key], value);
    }
  }

  return merged as T;
}
